import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFullScreen } from '../Layout';

interface PageShellProps {
  children: React.ReactNode;
  title?: string;
  largeTitle?: boolean;
  onBack?: () => void;
  backTo?: string;
  /** Label shown next to the back chevron (e.g. "Projekt"). Defaults to "Tillbaka". */
  backLabel?: string;
  headerRight?: React.ReactNode;
  headerContent?: React.ReactNode;
  bottomAction?: React.ReactNode;
  noPadding?: boolean;
  className?: string;
}

/** Height of the nav bar in px */
const NAV_HEIGHT = 44;

export function PageShell({
  children,
  title,
  largeTitle = false,
  onBack,
  backTo,
  backLabel = 'Tillbaka',
  headerRight,
  headerContent,
  bottomAction,
  noPadding = false,
  className = '',
}: PageShellProps) {
  const navigate = useNavigate();
  const { setHideHeader } = useFullScreen();
  const largeTitleRef = useRef<HTMLDivElement>(null);
  const [showInlineTitle, setShowInlineTitle] = useState(false);

  const hasBack = onBack || backTo;
  const showHeader = title || hasBack || headerRight || headerContent;

  // On mobile subpages (hasBack), hide the main Header via context
  useEffect(() => {
    if (!hasBack) return;
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [hasBack, setHideHeader]);

  useEffect(() => {
    if (!largeTitle || !largeTitleRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowInlineTitle(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: `-${NAV_HEIGHT + 16}px 0px 0px 0px` }
    );

    observer.observe(largeTitleRef.current);
    return () => observer.disconnect();
  }, [largeTitle]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {showHeader && (
        <>
          {/* Fixed nav bar — sits at very top on mobile subpages, below desktop sidebar header */}
          <div
            className={`fixed left-0 right-0 z-[35] bg-[var(--bg-surface)]/80 backdrop-blur-xl border-b border-[var(--border-default)] lg:left-[240px]`}
            style={{ top: 0, paddingTop: 'env(safe-area-inset-top, 0px)' }}
          >
            <div className="relative flex items-center h-11 px-4 max-w-5xl mx-auto w-full">
              {/* Left: iOS back button — chevron + label */}
              {hasBack && (
                <button
                  onClick={handleBack}
                  className="flex items-center -ml-1.5 min-w-0 active:opacity-50 transition-opacity z-10"
                  aria-label="Tillbaka"
                >
                  <ChevronLeft size={22} strokeWidth={2.5} className="text-accent-600 dark:text-accent-400 flex-shrink-0" />
                  <span className="text-[17px] text-accent-600 dark:text-accent-400 truncate max-w-[120px]">
                    {backLabel}
                  </span>
                </button>
              )}

              {/* Center: inline title (absolutely centered, iOS convention).
                  In largeTitle mode the &lt;h1&gt; below is the real heading and this
                  is its collapsed twin, so this one renders as a plain span and
                  is hidden from the a11y tree while faded out — otherwise screen
                  readers announce the same title twice, and two &lt;h1&gt;s appear on
                  one page. Without largeTitle this IS the page heading, so it
                  keeps the &lt;h1&gt;. */}
              {title && (
                largeTitle ? (
                  <span
                    aria-hidden={!showInlineTitle}
                    className={`absolute inset-x-0 text-center text-[17px] font-semibold text-[var(--text-primary)] truncate pointer-events-none px-28 transition-opacity duration-200 ${
                      showInlineTitle ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {title}
                  </span>
                ) : (
                  <h1 className="absolute inset-x-0 text-center text-[17px] font-semibold text-[var(--text-primary)] truncate pointer-events-none px-28 transition-opacity duration-200 opacity-100">
                    {title}
                  </h1>
                )
              )}

              {/* Spacer when no back button */}
              {!hasBack && <div className="flex-1" />}

              {/* Right: actions */}
              {headerRight && (
                <div className="flex items-center gap-1 ml-auto z-10">
                  {headerRight}
                </div>
              )}
            </div>
            {headerContent && (
              <div className="px-4 pb-3 max-w-5xl mx-auto w-full">
                {headerContent}
              </div>
            )}
          </div>

          {/* Spacer to push content below the fixed nav bar */}
          <div style={{ height: NAV_HEIGHT, paddingTop: 'env(safe-area-inset-top, 0px)' }} />
        </>
      )}

      <main className={`flex-1 ${noPadding ? '' : 'px-4 py-4 lg:px-6'} max-w-5xl mx-auto w-full`}>
        {largeTitle && title && (
          <div ref={largeTitleRef} className="pb-2">
            <h1 className="text-[34px] font-bold text-[var(--text-primary)] leading-tight tracking-tight">
              {title}
            </h1>
          </div>
        )}
        {children}
      </main>

      {bottomAction && (
        <div className="sticky bottom-0 z-10 bg-[var(--bg-surface)]/80 backdrop-blur-xl border-t border-[var(--border-default)]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="px-4 py-3 max-w-5xl mx-auto w-full">
            {bottomAction}
          </div>
        </div>
      )}
    </div>
  );
}
